import isEqual from 'lodash/isEqual';
import React, { useMemo, useState } from 'react';
import { FileUploadField, FileUploadFieldProps, type FileWithUrl } from '@placer-ui/components';
import {
    FILES_LIMIT,
    SF_FILE_SIZE_LIMIT_MB,
} from 'features/my-zone/support-requests/components/support-requests-modals/consts/consts';
import { FeedbackState } from '@placer-ui/components/dist/core-components/fields/feedback-message/feedback-message-types';
import { megaBytesToBytes } from 'utils/size/bytes-conversion';

type SupportRequestsFileUploadProps = FileUploadFieldProps & {
    withSizeLimitMessage?: boolean;
};

export const SupportRequestsFileUpload = ({
    maxFiles = FILES_LIMIT,
    maxSize = SF_FILE_SIZE_LIMIT_MB,
    onFailedUpload,
    feedback = [],
    setFiles,
    withSizeLimitMessage = true,
    ...rest
}: SupportRequestsFileUploadProps) => {
    const [fileUploadErrorMessages, setFileUploadErrorMessages] = useState<string[]>([]);

    const handleSetFiles = (files: FileWithUrl[]) => {
        if (files.length && !isEqual(rest.files, files)) {
            setFileUploadErrorMessages([]);
        }
        setFiles(files.filter(Boolean));
    };

    const errorMsgMap: Record<string, string> = {
        'Maximum files has been reached': `Upload limit exceeded; maximum is ${FILES_LIMIT} files.`,
        '.* has exceeded the maximum file size': `Upload failed. File size must not exceed ${SF_FILE_SIZE_LIMIT_MB}MB.`,
    };

    const handleFailedUpload = (reasons: string[]) => {
        const messages = reasons.map((reason) => {
            const message = Object.entries(errorMsgMap).find(([key]) =>
                new RegExp(key).test(reason),
            );
            return message?.[1] ?? reason;
        });
        setFileUploadErrorMessages(messages);
        onFailedUpload?.(reasons);
    };

    const feedBackMessages = useMemo(() => {
        const messages = [
            ...fileUploadErrorMessages.map((message) => ({
                message,
                state: 'error' as FeedbackState,
                hideIcon: true,
            })),
        ];

        if (withSizeLimitMessage) {
            messages.push({
                message: `File size up to ${SF_FILE_SIZE_LIMIT_MB}MB`,
                state: 'guidance' as FeedbackState,
                hideIcon: true,
            });
        }
        return messages;
    }, [fileUploadErrorMessages, withSizeLimitMessage]);

    return (
        <FileUploadField
            feedback={[...feedBackMessages, ...feedback]}
            onFailedUpload={handleFailedUpload}
            maxFiles={maxFiles}
            maxSize={megaBytesToBytes(SF_FILE_SIZE_LIMIT_MB)}
            setFiles={handleSetFiles}
            testId={'support-requests-file-upload'}
            {...rest}
        />
    );
};
